/**
 * Studio shell — renders the Canva-style left rail (desktop) and the fixed
 * bottom nav (mobile). Wrap any Studio page with <StudioShell>children</StudioShell>.
 *
 * Keeps paths like /ads-creator/* intact via aliases on /studio/*.
 */
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Plus, Home, Folder, LayoutGrid, Crown, MoreHorizontal,
  Image as ImageIcon, Film, Video, Megaphone, Sparkles, Trophy,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSelector } from "../components/common/LanguageSelector";
import { DEFAULT_LANGUAGES } from "../landing/constants";
import { LanguageArrivalBanner } from "../components/common/LanguageArrivalBanner";
import "./studio.css";

export function StudioShell({ children, activeKey }) {
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const BOTTOM_NAV = [
    { to: "/studio/create", label: t.studio_nav_create || "Create", icon: Plus, testid: "studio-nav-create" },
    { to: "/ads-creator/my-ads", label: t.studio_nav_mine || "My designs", icon: Folder, testid: "studio-nav-mine" },
    { to: "/ads-creator/templates", label: t.studio_nav_templates || "Templates", icon: LayoutGrid, testid: "studio-nav-templates" },
    { to: "/community/top-sharers", label: t.studio_nav_community || "Community", icon: Trophy, testid: "studio-nav-community" },
    { to: "/studio/more", label: t.studio_nav_more || "More", icon: MoreHorizontal, testid: "studio-nav-more" },
  ];

  const RAIL_SECONDARY = [
    { to: "/dashboard", label: t.studio_nav_home || "Home", icon: Home, testid: "studio-rail-home" },
    { to: "/ads-creator/my-ads", label: t.studio_nav_projects || "Projects", icon: Folder, testid: "studio-rail-projects" },
    { to: "/ads-creator/templates", label: t.studio_nav_templates || "Templates", icon: LayoutGrid, testid: "studio-rail-templates" },
    { to: "/community/top-sharers", label: t.studio_nav_community || "Community", icon: Trophy, testid: "studio-rail-community" },
    { to: "/studio/create/ad", label: t.studio_nav_ai_magic || "AI Magic", icon: Sparkles, testid: "studio-rail-ai" },
    { to: "/studio/team", label: t.studio_nav_business || "Business", icon: Crown, testid: "studio-rail-business" },
  ];

  function isActive(to) {
    const target = to.split("?")[0];
    const key = activeKey || location.pathname;
    return key === target || key.startsWith(target + "/");
  }

  return (
    <div className="studio-root" data-testid="studio-shell">
      {/* Desktop left rail */}
      <aside className="studio-sidebar" aria-label="Studio navigation">
        <Link to="/ads-creator" className="studio-brand" data-testid="studio-brand">
          <span className="studio-brand-mark">S</span>
          <span className="studio-brand-label">Studio</span>
        </Link>

        <button
          type="button"
          onClick={() => navigate("/studio/create")}
          className="studio-rail-primary"
          title={t.studio_nav_create || "Create"}
          aria-label={t.studio_nav_create_aria || "Create new design"}
          data-testid="studio-rail-create"
        >
          <Plus size={26} />
        </button>

        <ul className="studio-rail-items">
          {RAIL_SECONDARY.map(({ to, label, icon: Icon, testid }) => {
            const active = isActive(to);
            return (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  className={`studio-rail-item ${active ? "is-active" : ""}`}
                >
                  <Icon />
                  <span>{label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
        <div className="studio-rail-spacer" />
        <div className="studio-rail-lang" data-testid="studio-rail-lang">
          <LanguageSelector
            currentLang={language}
            onChangeLang={setLanguage}
            languages={DEFAULT_LANGUAGES}
          />
        </div>
      </aside>

      {/* Mobile top-right language picker — only visible below desktop breakpoint */}
      <div className="studio-mobile-lang" data-testid="studio-mobile-lang">
        <LanguageSelector
          currentLang={language}
          onChangeLang={setLanguage}
          languages={DEFAULT_LANGUAGES}
        />
      </div>

      {/* One-shot arrival banner: if detect-location disagrees with current UI
          language, surface a subtle switch CTA (shown once per session). */}
      <LanguageArrivalBanner />

      {/* Main content */}
      <main className="studio-main">{children}</main>

      {/* Mobile bottom nav */}
      <nav className="studio-bottom-nav" aria-label="Main navigation">
        {BOTTOM_NAV.map(({ to, label, icon: Icon, testid }) => {
          const active = isActive(to);
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={`studio-bottom-item ${active ? "is-active" : ""}`}
            >
              <span className="studio-bottom-icon">
                <Icon />
              </span>
              <span className="studio-bottom-label">{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

/** Exports used by StudioCreate for content-type cards */
export const CONTENT_TYPES = [
  { key: "post", label: "Post", desc: "Instagram 1:1 · Facebook 4:5",
    icon: ImageIcon, accent: "#f472b6",
    to: "/studio/create/post" },
  { key: "story", label: "Story", desc: "9:16 · IG / FB / TikTok",
    icon: ImageIcon, accent: "#a78bfa",
    to: "/studio/create/story" },
  { key: "reel", label: "Reel", desc: "9:16 animated · 15–60s",
    icon: Film, accent: "#a855f7",
    to: "/studio/create/reel" },
  { key: "video", label: "Video", desc: "16:9 · landscape",
    icon: Video, accent: "#60a5fa",
    to: "/studio/create/video" },
  { key: "ad", label: "Ad", desc: "FB/IG ad creative",
    icon: Megaphone, accent: "#fb923c",
    to: "/studio/create/ad" },
];
